import './DimQuantitativeChart.css';

import React from 'react';
import { getDimObjectiveType } from '../../objectives/helper';
import { calculateMinMaxValuesFromValuesArray, calculatePaddedDates, getDimensionValueName, quantValueWithUnit, renderSampleStatusTrend, renderTooltipData } from '../helper';
import { getGenericObjectivesSeries, getGenericSamplesSeries } from '../optionsHelper';
import B2BChart from '../B2BChart';
import Banner from '../../../../b2b-common/Banner';
import { renderToString } from 'react-dom/server';

const DimensionsOrderColorMap = {
    1: '#007aff',
    2: '#ffcc00',
    3: '#ff9500',
    4: '#4cd964',
    5: '#ff3b30',
    6: '#5856d6',
    7: '#34c759',
    8: '#ff2d55',
    9: '#5ac8fa',
    10: '#ff9500',
    11: '#ffcc00',
    12: '#007aff',
};

function renderDimQuantObjectiveTooltip(title, subtitle, date, value) {
    return (
        <div className='dim-quantitative-objective-tooltip'>
            <Banner message={title} underline={false} headingLevel='h5' className="dim-quantitative-objective-tooltip-banner-1" />
            <Banner message={subtitle} underline={false} headingLevel='h6' className="dim-quantitative-objective-tooltip-banner-2" />
            {renderTooltipData(date, value)}
        </div>
    );
}

function renderDimQuantSampleTooltip(title, subtitle, date, value, status, trend) {
    return (
        <div className='dim-quantitative-sample-tooltip'>
            <Banner message={title} underline={false} headingLevel='h5' className="dim-quantitative-objective-tooltip-banner-1" />
            <Banner message={subtitle} underline={false} headingLevel='h6' className="dim-quantitative-objective-tooltip-banner-2" />
            {renderTooltipData(date, value)}
            {renderSampleStatusTrend(status, trend)}
        </div>
    );
}

function renderDimQuantObjective(params, indicatorDTO) {
    const pointIndex = params.dataIndex;
    const date = new Date(params.data.value[0]).toLocaleDateString();
    const value = params.data.value[1];
    const dimensionOrder = params.data.dimensionOrder;
    const objectiveType = params.data.objectiveType;

    if (objectiveType === 'Regular-Dimensional') {
        if (pointIndex === 0) {
            return renderToString(renderDimQuantObjectiveTooltip('Objective Start', `Dimension ${dimensionOrder} - ${getDimensionValueName(dimensionOrder, indicatorDTO.dimensionalType)}`, date, quantValueWithUnit(value, indicatorDTO)));
        } else if (pointIndex === 1) {
            return renderToString(renderDimQuantObjectiveTooltip('Objective End', `Dimension ${dimensionOrder} - ${getDimensionValueName(dimensionOrder, indicatorDTO.dimensionalType)}`, date, quantValueWithUnit(value, indicatorDTO)));
        }
    } else if (objectiveType === 'Range-Dimensional') {
        if (pointIndex === 0) {
            return renderToString(renderDimQuantObjectiveTooltip('Objective Lower Range', `Dimension ${dimensionOrder} - ${getDimensionValueName(dimensionOrder, indicatorDTO.dimensionalType)}`, date, quantValueWithUnit(value, indicatorDTO)));
        } else if (pointIndex === 1) {
            return renderToString(renderDimQuantObjectiveTooltip('Objective Upper Range', `Dimension ${dimensionOrder} - ${getDimensionValueName(dimensionOrder, indicatorDTO.dimensionalType)}`, date, quantValueWithUnit(value, indicatorDTO)));
        }
    } else if ((objectiveType === 'Threshold_Above-Dimensional') || (objectiveType === 'Threshold_Below-Dimensional')) {
        if ((pointIndex === 0) || (pointIndex === 1)) {
            return renderToString(renderDimQuantObjectiveTooltip('Objective Threshold', `Dimension ${dimensionOrder} - ${getDimensionValueName(dimensionOrder, indicatorDTO.dimensionalType)}`, date, quantValueWithUnit(value, indicatorDTO)));
        }
    }
}

function renderDimQuantSample(params, indicatorDTO) {
    const date = new Date(params.data.value[0]).toLocaleDateString();
    const value = params.data.value[1];
    const dimensionOrder = params.data.dimensionOrder;
    const status = params.data.status;
    const trend = params.data.trend;

    return renderToString(renderDimQuantSampleTooltip('Sample', `Dimension ${dimensionOrder} - ${getDimensionValueName(dimensionOrder, indicatorDTO.dimensionalType)}`, date, quantValueWithUnit(value, indicatorDTO), status, trend));
}

const DimQuantitativeChart = ({ indicatorDTO, objective }) => {

    const objectiveType = getDimObjectiveType(objective);
    const dimensionalType = indicatorDTO.dimensionalType;

    const paddingPercentage = 0.05; // 5% padding
    const { paddedStart, paddedEnd } = (objectiveType === 'Regular-Dimensional' || objectiveType === 'Range-Dimensional') ?
        calculatePaddedDates(objective.startDate, objective.endDate, paddingPercentage) :
        (objectiveType === 'Threshold_Above-Dimensional' || objectiveType === 'Threshold_Below-Dimensional') ?
            calculatePaddedDates(objective.startDate, objective.endDate, paddingPercentage) :
            { paddedStart: objective.startDate, paddedEnd: objective.endDate };

    const samplesDataPerDimension = objective.valuesPerDimension.map(dimension => ({
        dimension: dimension.quantitativeDimensionOrder,
        samples: dimension.samplesPerObjective.map(sample => ({
            value: [sample.sampleDate, sample.quantitativeValue],
            isSample: true,
            dimensionOrder: dimension.quantitativeDimensionOrder,
            status: sample.status,
            trend: sample.trend
        }))
    }));

    const samplesSeries = samplesDataPerDimension.map(dimension =>
        getGenericSamplesSeries(
            `Dimension ${dimension.dimension}`,
            dimension.samples,
            DimensionsOrderColorMap[dimension.dimension])
    );

    const objectiveValuesPerDimension = (objectiveType === 'Regular-Dimensional' || objectiveType === 'Range-Dimensional') ?
        objective.valuesPerDimension.map(dimension => ({
            dimension: dimension.quantitativeDimensionOrder,
            values: [
                {
                    value: [objective.startDate, dimension.startQuantitativeValue],
                    isObjective: true,
                    dimensionOrder: dimension.quantitativeDimensionOrder,
                    objectiveType: objectiveType,
                },
                {
                    value: [objective.endDate, dimension.endQuantitativeValue],
                    isObjective: true,
                    dimensionOrder: dimension.quantitativeDimensionOrder,
                    objectiveType: objectiveType,
                }
            ]
        })) :
        (objectiveType === 'Threshold_Above-Dimensional' || objectiveType === 'Threshold_Below-Dimensional') ?
            objective.valuesPerDimension.map(dimension => ({
                dimension: dimension.quantitativeDimensionOrder,
                values: [
                    {
                        value: [objective.startDate, dimension.startQuantitativeValue],
                        isObjective: true,
                        dimensionOrder: dimension.quantitativeDimensionOrder,
                        objectiveType: objectiveType,
                    },
                    {
                        value: [objective.endDate, dimension.startQuantitativeValue],
                        isObjective: true,
                        dimensionOrder: dimension.quantitativeDimensionOrder,
                        objectiveType: objectiveType,
                    }
                ]
            })) : [];

    const objectiveSeries = objectiveValuesPerDimension.map(dimension =>
        getGenericObjectivesSeries(
            `D${dimension.dimension} - ${getDimensionValueName(dimension.dimension, dimensionalType)} - Objective`,
            dimension.values,
            DimensionsOrderColorMap[dimension.dimension], objectiveType)
    );

    const allValues = [
        ...samplesDataPerDimension.map(dimension => dimension.samples.map(sample => sample.value[1])).flat(),
        ...objectiveValuesPerDimension.map(dimension => dimension.values.map(value => value.value[1])).flat()
    ];

    const { minValue, maxValue } = calculateMinMaxValuesFromValuesArray(allValues, paddingPercentage);

    const options = {
        grid: {
            top: '5%',
            left: '8%',
            right: '5%',
            bottom: '7%'
        },
        xAxis: {
            type: 'time',
            min: paddedStart,
            max: paddedEnd
        },
        yAxis: {
            type: 'value',
            min: minValue,
            max: maxValue,
        },
        tooltip: {
            trigger: 'item',
            formatter: function (params) {
                if (params.data.isObjective) {
                    return renderDimQuantObjective(params, indicatorDTO);
                } else if (params.data.isSample) {
                    return renderDimQuantSample(params, indicatorDTO);
                }
                return '';
            }
        },
        series: [
            ...objectiveSeries,
            ...samplesSeries,
        ]
    };

    return (
        <div className='dim-quantitative-chart'>
            <B2BChart
                options={options}
                enableZoom={true}
                className='dim-quantitative-chart'
            />
        </div>
    );
};

export default DimQuantitativeChart;